import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionReturnComunicationForm, { ReturnComunicationVM } from './form';

interface ReturnComunicationProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionReturnComunicationComponent = ({
  loadUpdateDate,
}: ReturnComunicationProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);

  const params = useParams();

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const registerOperation = useHttpClient(
    `${__PRACTICE_API_URL__}/registrazione-operazione/ritorno/comunicazione-ostativa`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<ReturnComunicationVM>({
    initialValues: {
      date: DateTime.now(),
      note: '',
    },
    validationSchema: yup.object().shape({
      date: yup.date().nullable().typeError('Data non valida').required('Il campo è obbligatorio'),
      note: yup.string().required('Il campo è obbligatorio'),
    }),
    onSubmit: (values) => {
      (
        registerOperation({
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tipoPratica: params.tipoPratica || undefined,
            descrizioneNote: values.note || undefined,
            tipoOperazione: '29',
            tipoStatoPratica: '1',
            codiceSospensione: 2,
            timestampSospensione: values?.date ? values?.date?.toFormat('yyyy-MM-dd') : undefined,
          },
        }) as ResponsePromise<void>
      ).then(async () => {
        toast.success('Operazione eseguita con successo');

        // aggiornamento stato pratica
        formContextPage.setFieldValue('practice', {
          ...(formContextPage.values.practice || {}),
          codiceStatoPratica: '1',
          tipoOperazione: '29',
        });

        // se cambia lo stato della pratica ricaricare dati
        const newData: PracticeDetailVM | void = await loadUpdateDate();

        if (newData) {
          formContextPage.setValues({
            ...(formContextPage.values || {}),
            ...newData,
          });
        }
      });
    },
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-return-comunication-content"
          id="panel-return-comunication-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Ritorno Comunicazione Ostativa (Art. 10 Bis)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionReturnComunicationForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => formContext.handleSubmit()}
              id="Return"
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionReturnComunicationComponent.displayName = 'Accordion Return comunication component';

const AccordionReturnComunication = memo(AccordionReturnComunicationComponent);

export default AccordionReturnComunication;
